import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { Field } from '#src/components/form/Form/Field/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { OffRampStepLayout } from '#src/components/offRamp/OffRampKycOnboarding/OffRampStepLayout/index.tsx';
import { useCreateOffRampBridgeCustomer } from '#src/components/offRamp/OffRampKycOnboarding/useCreateOffRampBridgeCustomer.tsx';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useIntlCountries } from '#src/hooks/useIntlCountries.ts';
import { glossary } from '#src/lib/glossary.ts';

import type {
  OffRampAddressDefaultsQuery,
  OffRampAddressDefaultsQueryVariables,
} from './__generated__/index.graphql.ts';

const OFF_RAMP_ADDRESS_DEFAULTS_QUERY = gql`
  query OffRampAddressDefaultsQuery {
    currentUser {
      slug
      myAccounts {
        id
        accountable {
          ... on Node {
            id
          }
          ... on PrivateFiatWalletAccount {
            id
            depositBankAccount {
              ... on BankAccountInterface {
                id
                ownerAddress {
                  addressLine1
                  addressLine2
                  city
                  region
                  postalCode
                  country
                }
              }
            }
          }
        }
      }
      userSettings {
        id
        postalAddress {
          streetAddress
          additionalAddress
          city
          region
          zipcode
          countryCode
        }
      }
    }
  }
` as TypedDocumentNode<
  OffRampAddressDefaultsQuery,
  OffRampAddressDefaultsQueryVariables
>;

type AddressDefaults = {
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
};

const toDefaults = (
  data: OffRampAddressDefaultsQuery | undefined
): AddressDefaults => {
  // Prefer the Mangopay deposit account's owner address — it's the address
  // Mangopay matched the user's KYC identity documents against. postalAddress is
  // only a shipping address for physical rewards, so it's the last-resort fallback.
  const ownerAddress = (data?.currentUser?.myAccounts ?? [])
    .map(({ accountable }) =>
      accountable && 'depositBankAccount' in accountable
        ? accountable.depositBankAccount?.ownerAddress
        : null
    )
    .find(Boolean);

  if (ownerAddress) {
    return {
      streetLine1: ownerAddress.addressLine1 ?? '',
      streetLine2: ownerAddress.addressLine2 ?? '',
      city: ownerAddress.city ?? '',
      state: ownerAddress.region ?? '',
      postalCode: ownerAddress.postalCode ?? '',
      countryCode: ownerAddress.country?.toLowerCase() ?? '',
    };
  }

  const postalAddress = data?.currentUser?.userSettings?.postalAddress;
  return {
    streetLine1: postalAddress?.streetAddress ?? '',
    streetLine2: postalAddress?.additionalAddress ?? '',
    city: postalAddress?.city ?? '',
    state: postalAddress?.region ?? '',
    postalCode: postalAddress?.zipcode ?? '',
    countryCode: postalAddress?.countryCode ?? '',
  };
};

type Props = {
  signedAgreementId: string;
  onComplete: () => void;
};

type AddressFormValues = {
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export const AddressStep = ({ signedAgreementId, onComplete }: Props) => {
  const { formatMessage } = useIntl();
  const createCustomer = useCreateOffRampBridgeCustomer();
  const countries = useIntlCountries(true);
  const { data, loading } = useQuery(OFF_RAMP_ADDRESS_DEFAULTS_QUERY);

  const [error, setError] = useState<string | null>(null);

  // Stable identity: Field re-registers (and triggers a form re-render) whenever
  // its `validate` prop changes, so an inline function would loop indefinitely.
  const validateCountry = useCallback(
    (value: string) =>
      value ? null : formatMessage({ defaultMessage: 'Select a country' }),
    [formatMessage]
  );

  if (loading && !data) {
    return <LoadingIndicator fullHeight />;
  }

  const defaults = toDefaults(data);
  const countryDefault = defaults.countryCode
    ? countries.find(({ value }) => value === defaults.countryCode)
    : undefined;

  return (
    <OffRampStepLayout
      title={<FormattedMessage defaultMessage="Your residential address" />}
      description={
        <FormattedMessage defaultMessage="We need your home address to register you with our payment partner." />
      }
    >
      <GraphqlForm
        onSubmit={async (values: AddressFormValues, onResult) => {
          setError(null);
          const result = await createCustomer({
            signedAgreementId,
            residentialAddress: {
              streetLine1: values.streetLine1,
              streetLine2: values.streetLine2 || null,
              city: values.city,
              state: values.state || null,
              postalCode: values.postalCode,
              country: values.country.toUpperCase(),
            },
          });

          const payload = result.data?.createOffRampBridgeCustomer;
          if (payload?.errors?.length) {
            setError(payload.errors[0].message);
          }

          onResult({ errors: payload?.errors ?? null });
        }}
        onSuccess={onComplete}
        render={(_Error, SubmitButton) => (
          <Vertical gap={2}>
            {!!error && <BodyM color="var(--c-red-400)">{error}</BodyM>}
            <TextField
              name="streetLine1"
              required
              defaultValue={defaults.streetLine1}
              label={<FormattedMessage defaultMessage="Street address" />}
            />
            <TextField
              name="streetLine2"
              defaultValue={defaults.streetLine2}
              label={
                <FormattedMessage defaultMessage="Apartment, suite, etc. (optional)" />
              }
            />
            <TextField
              name="postalCode"
              required
              defaultValue={defaults.postalCode}
              label={<FormattedMessage defaultMessage="Postal code" />}
            />
            <TextField
              name="city"
              required
              defaultValue={defaults.city}
              label={<FormattedMessage defaultMessage="City" />}
            />
            <TextField
              name="state"
              defaultValue={defaults.state}
              label={
                <FormattedMessage defaultMessage="State / region (optional)" />
              }
            />
            <Field
              name="country"
              defaultValue={countryDefault?.value ?? ''}
              validate={validateCountry}
              render={({ error: countryError, handleChange, value }) => (
                <Vertical gap={0.5}>
                  <BodyM bold color="currentColor" as="label" htmlFor="country">
                    <FormattedMessage defaultMessage="Country" />
                  </BodyM>
                  <NativeSelect
                    fullWidth
                    value={(value as string) || ''}
                    onChange={handleChange}
                    values={countries}
                    placeholder={formatMessage({
                      defaultMessage: 'Choose a country',
                    })}
                  />
                  {!!countryError && (
                    <BodyM color="var(--c-red-400)">{countryError}</BodyM>
                  )}
                </Vertical>
              )}
            />
            <SubmitButton size="medium" fullWidth>
              <FormattedMessage {...glossary.continue} />
            </SubmitButton>
          </Vertical>
        )}
      />
    </OffRampStepLayout>
  );
};
